import type { ScoreCriterion, ScoreEntity, WhereGroup } from '@parcel/filters';
import { Button, Input } from '@parcel/ui';
import { Plus, Trash2, X } from 'lucide-react';
import type { WhereOptions } from '../where-bar/where-menu.js';
import { CardIcon } from './criteria-catalog.js';
import type { CardKind } from './criteria-state.js';
import { CriterionBuilder } from './criterion-builder.js';
import { ImpactSelect } from './impact-select.js';

export interface CardGroup {
  key: string;
  card: CardKind;
  title: string;
  criteria: ScoreCriterion[];
}

const pickOf = (card: CardKind) =>
  card.type === 'field' && card.key === 'view' ? ({ type: 'view' } as const) : card;

/** "812 match", muted; hidden until the preview has counted. */
function Matches({ n }: { n: number | undefined }) {
  if (n === undefined) return null;
  return (
    <span className="whitespace-nowrap text-xs text-muted-foreground">
      {n.toLocaleString('en-US')} match
    </span>
  );
}

/** One card per thing the score reads; each row is one criterion with its own condition and impact. */
export function CriterionCard({
  entity,
  group,
  issues,
  matches,
  disabled,
  options,
  onChange,
  onRemove,
  onAddRow,
  canAddRow = true,
}: {
  entity: ScoreEntity;
  group: CardGroup;
  issues: ReadonlyMap<string, string>;
  /** Per criterion id, how many records it matches in the preview. */
  matches?: ReadonlyMap<string, number>;
  disabled?: boolean;
  options: WhereOptions;
  onChange: (id: string, patch: Partial<ScoreCriterion>) => void;
  onRemove: (ids: string[]) => void;
  onAddRow: () => void;
  /** False at the score's criteria cap. */
  canAddRow?: boolean;
}) {
  const { card, criteria } = group;
  const custom = card.type === 'custom' ? criteria[0] : undefined;
  return (
    <section
      className="space-y-3 rounded-lg border bg-card p-4"
      data-testid={`criterion-card-${group.key}`}
    >
      <header className="flex flex-wrap items-center gap-2">
        <CardIcon pick={pickOf(card)} />
        {custom ? (
          <>
            <span className="text-sm font-medium">Custom condition</span>
            <Input
              aria-label="Condition name"
              value={custom.label ?? ''}
              placeholder="Name this condition"
              maxLength={80}
              disabled={disabled}
              onChange={(e) => onChange(custom.id, { label: e.target.value })}
              className="h-8 min-w-32 max-w-[260px] flex-1"
            />
            <span className="flex-1" />
            <Matches n={matches?.get(custom.id)} />
            <span className="text-xs text-muted-foreground">Impact</span>
            <ImpactSelect
              value={custom.impact}
              disabled={disabled}
              onChange={(impact) => onChange(custom.id, { impact })}
            />
          </>
        ) : (
          <h4 className="flex-1 text-sm font-medium">{group.title}</h4>
        )}
        {disabled ? null : (
          <Button
            variant="ghost"
            size="icon"
            className="size-8 text-muted-foreground"
            aria-label={`Remove ${group.title}`}
            onClick={() => onRemove(criteria.map((c) => c.id))}
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </header>
      <div className="space-y-3">
        {criteria.map((criterion) => {
          const issue = issues.get(criterion.id);
          const builder = (
            <CriterionBuilder
              entity={entity}
              where={criterion.where}
              onChange={(where: WhereGroup) => onChange(criterion.id, { where })}
              mode={card.type}
              {...(card.type === 'field' ? { lockedField: card.key } : {})}
              {...(card.type === 'relation' ? { relation: card.relation } : {})}
              options={options}
              disabled={disabled}
            />
          );
          return (
            <div key={criterion.id} className="space-y-1" data-testid={`criterion-${criterion.id}`}>
              {custom ? (
                builder
              ) : (
                // Narrow (Spec open beside the page), the count and impact take their own line.
                <div className="flex flex-wrap items-start gap-2">
                  <div className="min-w-[min(100%,18rem)] flex-1">{builder}</div>
                  <div className="ml-auto flex shrink-0 items-center gap-2">
                    <Matches n={matches?.get(criterion.id)} />
                    <span className="text-xs text-muted-foreground">Impact</span>
                    <ImpactSelect
                      value={criterion.impact}
                      disabled={disabled}
                      onChange={(impact) => onChange(criterion.id, { impact })}
                    />
                    {disabled ? null : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        aria-label="Remove criterion"
                        onClick={() => onRemove([criterion.id])}
                      >
                        <X className="size-4" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {issue ? <p className="text-xs text-destructive">{issue}</p> : null}
            </div>
          );
        })}
      </div>
      {disabled || custom ? null : (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2"
          disabled={!canAddRow}
          onClick={onAddRow}
        >
          <Plus className="size-4" />
          Add more values
        </Button>
      )}
    </section>
  );
}
